// FinanceTab — the contract-detail Finance panel. Owns data-loading (the PO
// list, the derived finance summary, and the milestone/SOW tracking lists the
// tag pickers need) and orchestrates the display shell: a contract-scoped
// rollup strip, a PO context bar, the position banner for the selected PO,
// and a sub-view toggle (Invoices | Budget lines | Rate card). The
// Budget-lines sub-view renders the read-only register (BudgetLines), whose
// expanded lines list the invoices drawing on each line — clicking one flips
// back to the Invoices sub-view with that row focused (the focus-request
// handshake below); the
// Rate-card sub-view renders the contract's standing rate catalogue (RateCard)
// and is contract-scoped (does not depend on a selected PO). PO create/edit/
// delete is driven through PurchaseOrderFormModal, whose mutation callbacks
// are wired to the finance hooks here (so the invalidate-on-success refetch
// owns the derived balances — the modal never reads a mutation response). The
// rate-item/milestone/SOW tag option lists (rateCard off the finance summary;
// milestones/SOWs off useFlatTrackingList, which drains every page — a busy
// prod contract carries more milestones than one 50-item page, and a
// single-page fetch silently truncated the pickers) are assembled here and
// threaded down to every consumer that resolves or edits a line/charge's
// tags. Milestone options carry the milestone number and sort in register
// order; SOW options are "number · name", numeric-aware sorted. Selection is
// driven by the ?po= URL param so a PO is shareable/bookmarkable; it defaults
// to the most recent open PO.

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { clsx } from "clsx";
import { FileText, ListChecks, Plus, Tags } from "lucide-react";
import { LoadingSpinner, Alert } from "../../common";
import Button from "../../common/Button";
import PoContextBar from "./PoContextBar";
import ContractRollupStrip from "./ContractRollupStrip";
import FinancePositionBanner from "./FinancePositionBanner";
import BudgetLines from "./BudgetLines";
import RateCard from "./RateCard";
import InvoiceQueue, { type InvoiceFocusRequest } from "./InvoiceQueue";
import PurchaseOrderFormModal, { type PoFormMode } from "./PurchaseOrderFormModal";
import RecordInvoiceModal, { type InvoiceFormMode } from "./RecordInvoiceModal";
import EmailPreviewModal from "./EmailPreviewModal";
import type { TagOption } from "./TagMultiSelect";
import {
  useFinanceSummary,
  usePurchaseOrders,
  useInvoices,
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
  useDeletePurchaseOrder,
} from "../../../hooks/useFinance";
import { useFlatTrackingList } from "../../../hooks/useFlatTrackingList";
import { currentSowValue } from "../tracking/variations-sows/contractValueDerivation";
import { formatMilestoneNumber } from "../tracking/milestone/milestoneFormState";
import { compareMilestoneNumber } from "../tracking/milestone/sortMilestones";
import { compareSowNumbers } from "../tracking/shared/sowPickerSearch";
import type { Milestone, Sow, Variation } from "../../../types";
import type {
  CreatePurchaseOrderInput,
  PurchaseOrderWithDerived,
  UpdatePurchaseOrderInput,
  InvoiceWithDerived,
  ApproveInvoiceResponse,
  Payables,
  ContractRollup,
} from "../../../types/finance";

const EMPTY_PAYABLES: Payables = { milestones: [], products: [], other: [], sows: [] };
const EMPTY_ROLLUP: ContractRollup = {
  committed: 0,
  contractValue: 0,
  remaining: 0,
  overCommitted: false,
};

interface FinanceTabProps {
  contractId: string;
  /** Parent contract — its stored GST intent drives the position banner's
   *  ex-GST meta (PO GST was removed at the header level; GST is decided per
   *  PoLine now, so the banner has no per-PO flag to read). */
  contract?: { gstApplies?: boolean };
  /** ?item= from the page URL (global-search deep link): the invoice to
   *  reveal on arrival. Seeds a one-shot focus request; the invoices
   *  sub-view is already the default. */
  itemIdFromUrl?: string | null;
}

type SubView = "budget-lines" | "invoices" | "rate-card";

// Recency key: issued date if present, else the created timestamp. Compared as
// ISO strings so lexical order matches chronological order.
function recencyKey(po: PurchaseOrderWithDerived): string {
  return po.issuedDate ?? po.createdAt;
}

// Default selection: the most recent OPEN purchase order, falling back to the
// most recent of any status when none are open. Undefined only when the list
// is empty.
function defaultPo(
  pos: PurchaseOrderWithDerived[],
): PurchaseOrderWithDerived | undefined {
  const pool = pos.some((po) => po.status === "open")
    ? pos.filter((po) => po.status === "open")
    : pos;
  return [...pool].sort((a, b) =>
    recencyKey(b).localeCompare(recencyKey(a)),
  )[0];
}

// ?po= wins when it matches a loaded PO, otherwise the default applies. A stale
// ?po= pointing at a since-deleted PO falls through to the default. Returns
// undefined only for an empty list — the single reachable guard in render.
function resolveSelectedPo(
  pos: PurchaseOrderWithDerived[],
  poParam: string | null,
): PurchaseOrderWithDerived | undefined {
  if (poParam) {
    const match = pos.find((po) => po.id === poParam);
    if (match) return match;
  }
  return defaultPo(pos);
}

const SUB_VIEWS: ReadonlyArray<{
  value: SubView;
  label: string;
  Icon: typeof FileText;
}> = [
  { value: "invoices", label: "Invoices", Icon: FileText },
  { value: "budget-lines", label: "Budget lines", Icon: ListChecks },
  { value: "rate-card", label: "Rate card", Icon: Tags },
];

export default function FinanceTab({
  contractId,
  contract,
  itemIdFromUrl,
}: FinanceTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  // The summary carries the payables picker, the contract-wide rollup
  // (ContractRollupStrip), and the rate card (rateItemOptions below). The PO
  // list GET carries the authoritative derived `position` this shell renders.
  const summaryQuery = useFinanceSummary(contractId);
  const posQuery = usePurchaseOrders(contractId);
  // The invoice set is loaded here (deduped with the queue's own fetch by React
  // Query) so the record-invoice modal can surface per-line contention with
  // other in-review invoices on the PO.
  const invoicesQuery = useInvoices(contractId);
  // Milestone/SOW tag option lists for the PO-line and invoice-charge tag
  // pickers — resolved contract-wide (not per-PO) since a line's tags can
  // reference any milestone/SOW on the contract. Full page drain, not a
  // single-page fetch: the backend list defaults to 50 items per page, and a
  // contract with more milestones than that would silently lose the rest
  // from the pickers.
  const milestonesQuery = useFlatTrackingList<Milestone>(
    contractId,
    "milestone",
  );
  const sowsQuery = useFlatTrackingList<Sow>(contractId, "sow");
  // Variations feed the SOW options' current-value derivation — a SOW tag's
  // lump-sum reference must reflect approved variations, not the stored
  // original (currentSowValue ignores proposed/rejected/archived itself).
  const variationsQuery = useFlatTrackingList<Variation>(
    contractId,
    "variation",
  );
  const createPo = useCreatePurchaseOrder();
  const updatePo = useUpdatePurchaseOrder();
  const deletePo = useDeletePurchaseOrder();

  // Invoices is the manager's primary finance surface, so it leads; Budget
  // lines and Rate card stay a click away on the toggle.
  const [subView, setSubView] = useState<SubView>("invoices");
  const [modalMode, setModalMode] = useState<PoFormMode | null>(null);
  const [invoiceModal, setInvoiceModal] = useState<{
    mode: InvoiceFormMode;
    source?: InvoiceWithDerived;
  } | null>(null);
  // onApproved (from the queue's approve action and the record modal's approve)
  // funnels here and opens the EmailPreviewModal with the approve response.
  const [approvedResponse, setApprovedResponse] =
    useState<ApproveInvoiceResponse | null>(null);
  // A budget-line invoice click-through (or a ?item= deep link, seeded at
  // mount): flips the sub-view to invoices and asks the queue to reveal the
  // row. Each request is a fresh object — the queue applies it exactly once
  // by identity and reports back via onFocusApplied, whereupon the request
  // is dropped so a queue remount (sub-view flip and back) can never
  // re-apply it over later manual filtering.
  const [focusRequest, setFocusRequest] = useState<InvoiceFocusRequest | null>(
    () => (itemIdFromUrl ? { id: itemIdFromUrl } : null),
  );
  // Render-time prev-value guard: a palette selection made while this tab is
  // already mounted changes ?item= without a remount, so the deep link must
  // re-seed on prop change, not only in the initializer — mirroring how the
  // tracking tabs react to itemIdFromUrl.
  const [lastItemIdFromUrl, setLastItemIdFromUrl] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  if ((itemIdFromUrl ?? null) !== lastItemIdFromUrl) {
    setLastItemIdFromUrl(itemIdFromUrl ?? null);
    if (itemIdFromUrl) {
      setFocusRequest({ id: itemIdFromUrl });
      setSubView("invoices");
    }
  }

  const purchaseOrders = posQuery.data?.purchaseOrders ?? [];
  const payables = summaryQuery.data?.payables ?? EMPTY_PAYABLES;
  const invoices = invoicesQuery.data?.invoices ?? [];
  const rateItemOptions: TagOption[] = useMemo(
    () =>
      (summaryQuery.data?.rateCard ?? []).map((r) => ({
        id: r.id,
        label: r.item,
        value: r.unitPrice,
        valueKind: "unitRate" as const,
      })),
    [summaryQuery.data],
  );
  // Register order (number then insertion suffix, so 2 < 10 and 13 < 13a),
  // with the number leading the label — pickers over a large milestone
  // register are scanned and searched by number.
  const milestoneOptions: TagOption[] = useMemo(
    () =>
      (milestonesQuery.data ?? [])
        .slice()
        .sort(compareMilestoneNumber)
        .map((m) => ({
          id: m.id,
          label: `${formatMilestoneNumber(m)} · ${m.name}`,
          value: m.paymentAmount ?? null,
          valueKind: "total" as const,
        })),
    [milestonesQuery.data],
  );
  const sowOptions: TagOption[] = useMemo(() => {
    const variations = variationsQuery.data ?? [];
    return (sowsQuery.data ?? [])
      .slice()
      .sort((a, b) => compareSowNumbers(a.number ?? a.name, b.number ?? b.name))
      .map((s) => ({
        id: s.id,
        label: s.number ? `${s.number} · ${s.name}` : s.name,
        value: currentSowValue(s, variations),
        valueKind: "total" as const,
      }));
  }, [sowsQuery.data, variationsQuery.data]);

  const poParam = searchParams.get("po");
  const selectedPo = resolveSelectedPo(purchaseOrders, poParam);

  const handleSelectPo = (poId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("po", poId);
    setSearchParams(next, { replace: true });
  };

  const isSubmitting =
    createPo.isPending || updatePo.isPending || deletePo.isPending;

  const handleCreate = (input: CreatePurchaseOrderInput) =>
    createPo.mutateAsync({ contractId, input });
  const handleUpdate = (input: UpdatePurchaseOrderInput) =>
    updatePo.mutateAsync({ contractId, poId: selectedPo!.id, input });
  const handleDelete = () =>
    deletePo.mutateAsync({ contractId, poId: selectedPo!.id });

  const poModal = (
    <PurchaseOrderFormModal
      isOpen={modalMode !== null}
      mode={modalMode ?? "create"}
      onClose={() => setModalMode(null)}
      source={modalMode === "edit" ? selectedPo : undefined}
      contractGstApplies={contract?.gstApplies}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      isSubmitting={isSubmitting}
      rateItemOptions={rateItemOptions}
      milestoneOptions={milestoneOptions}
      sowOptions={sowOptions}
    />
  );

  // The tracking drains gate the tab alongside the finance queries: a failed
  // or unfinished milestone/SOW/variation drain would otherwise render the
  // PO-line and invoice-charge tag pickers with silently missing payable
  // options. Holding the whole tab also means no finance form can open with
  // incomplete tracking inputs.
  if (
    posQuery.isPending ||
    summaryQuery.isPending ||
    milestonesQuery.isPending ||
    sowsQuery.isPending ||
    variationsQuery.isPending
  ) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (
    posQuery.error ||
    summaryQuery.error ||
    milestonesQuery.error ||
    sowsQuery.error ||
    variationsQuery.error
  ) {
    return (
      <Alert variant="error">
        Couldn't load finance data. Please try again.
      </Alert>
    );
  }

  if (!selectedPo) {
    return (
      <>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-12 text-center dark:border-slate-700 dark:bg-slate-800">
          <h3 className="text-h4 text-slate-900 dark:text-slate-100">
            No purchase orders yet
          </h3>
          <p className="mt-1 text-body text-slate-500 dark:text-slate-400">
            Purchase orders and their budget lines will appear here once one is
            added.
          </p>
          <div className="mt-4 flex justify-center">
            <Button
              icon={<Plus className="h-4 w-4" aria-hidden="true" />}
              onClick={() => setModalMode("create")}
            >
              Add purchase order
            </Button>
          </div>
        </div>
        {poModal}
      </>
    );
  }

  // PO GST was removed at the header level (GST is decided per PoLine now),
  // so the banner's ex-GST meta falls back straight to the contract's own
  // stored intent.
  const bannerGstApplies = contract?.gstApplies;
  const contractRollup = summaryQuery.data?.contractRollup ?? EMPTY_ROLLUP;
  const lineCount = selectedPo.lines.length;
  // The invoice queue is contract-wide, so an edited invoice may belong to a
  // PO other than the one currently selected in the context bar. Resolve the
  // modal's PO from the invoice's own poId in that case — passing selectedPo
  // would show the wrong PO's lines and let the overdraw gate silently skip
  // splits it can't find a line for. Create mode always targets selectedPo.
  const invoiceModalPo =
    invoiceModal?.mode === "edit"
      ? (purchaseOrders.find((po) => po.id === invoiceModal.source?.poId) ??
        selectedPo)
      : selectedPo;

  return (
    <>
    <div className="space-y-4">
      <ContractRollupStrip rollup={contractRollup} />

      <PoContextBar
        purchaseOrders={purchaseOrders}
        selectedPoId={selectedPo.id}
        onSelectPo={handleSelectPo}
        onAddPo={() => setModalMode("create")}
      />

      <FinancePositionBanner
        position={selectedPo.position}
        gstApplies={bannerGstApplies}
      />

      <div
        role="group"
        aria-label="Finance sub-view"
        className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800"
      >
        {SUB_VIEWS.map(({ value, label, Icon }) => {
          const active = subView === value;
          const count = value === "budget-lines" ? lineCount : undefined;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              onClick={() => setSubView(value)}
              className={clsx(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
                active
                  ? "bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200",
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{label}</span>
              {count !== undefined && (
                <span className="tabular-nums text-slate-500 dark:text-slate-500">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {subView === "budget-lines" ? (
        <BudgetLines
          po={selectedPo}
          invoices={invoices}
          onManagePo={() => setModalMode("edit")}
          onOpenInvoice={(invoiceId) => {
            setFocusRequest({ id: invoiceId });
            setSubView("invoices");
          }}
          rateItemOptions={rateItemOptions}
          milestoneOptions={milestoneOptions}
          sowOptions={sowOptions}
        />
      ) : subView === "rate-card" ? (
        <RateCard contractId={contractId} />
      ) : (
        <InvoiceQueue
          contractId={contractId}
          selectedPo={selectedPo}
          purchaseOrders={purchaseOrders}
          onRecordInvoice={() => setInvoiceModal({ mode: "create" })}
          onEditInvoice={(invoice) =>
            setInvoiceModal({ mode: "edit", source: invoice })
          }
          onApproved={setApprovedResponse}
          focusRequest={focusRequest}
          onFocusApplied={() => setFocusRequest(null)}
        />
      )}
    </div>
    {poModal}
    <RecordInvoiceModal
      isOpen={invoiceModal !== null}
      onClose={() => setInvoiceModal(null)}
      mode={invoiceModal?.mode ?? "create"}
      contractId={contractId}
      po={invoiceModalPo}
      purchaseOrders={purchaseOrders}
      payables={payables}
      invoices={invoices}
      source={invoiceModal?.mode === "edit" ? invoiceModal.source : undefined}
      onApproved={setApprovedResponse}
    />
    {approvedResponse && (
      <EmailPreviewModal
        isOpen
        invoiceNumber={approvedResponse.invoiceNumber}
        email={approvedResponse.email}
        onClose={() => setApprovedResponse(null)}
      />
    )}
    </>
  );
}
